import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, 
  ThumbsUp, 
  ThumbsDown, 
  SkipForward,
  Clock, 
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import {
  sortPlaylistByPriority,
  calculateSkipProgress,
  getScoreColor,
  formatVoteDisplay,
  buildPlaylistArray
} from '../utils/playlistScoring';
import { canManageQueue } from '../utils/roles';
import { useArtworkColor } from '../hooks/useArtworkColor';
import PartyEnergyMeter from './PartyEnergyMeter';
import Avatar, { AvatarStack } from './Avatar';
import toast from 'react-hot-toast';

const PlaylistEnhanced = ({ session, currentPlayingSongId }) => {
  const { user } = useAuth();
  const { voteSong, hostAction, reactToSong } = useSocket();
  const [showPlayed, setShowPlayed] = useState(false);
  // Optimistic overrides only — server data (session.votes) is the truth,
  // these bridge the gap until the round-trip lands / next refetch
  const [localVotes, setLocalVotes] = useState({});
  const [localSkipVotes, setLocalSkipVotes] = useState({});

  // Hosts AND co-hosts get the queue controls (boost / play next / remove)
  const isHost = canManageQueue(session, user?.uid);

  // Transform playlist data: vote counts come from session.votes via
  // buildPlaylistArray, with local optimistic votes layered on top
  const playlistArray = useMemo(() => {
    return buildPlaylistArray(session, user?.uid).map((item) => {
      const key = item.firebaseKey;
      const serverVote = item.userVote;
      const localVote = localVotes[key];

      let { upvotes, downvotes } = item;
      if (localVote !== undefined && localVote !== serverVote) {
        // Swap our server-recorded vote for the local one in the counts
        if (serverVote === 1) upvotes -= 1;
        else if (serverVote === -1) downvotes -= 1;
        if (localVote === 1) upvotes += 1;
        else if (localVote === -1) downvotes += 1;
      }

      const serverSkipCount = session.skipVotes?.[key]?._count || 0;
      const localSkips = localSkipVotes[key] || [];

      return {
        ...item,
        upvotes,
        downvotes,
        userVote: localVote !== undefined ? localVote : serverVote,
        skipVotes: localSkips,
        skipCount: Math.max(serverSkipCount, localSkips.length)
      };
    });
  }, [session, user?.uid, localVotes, localSkipVotes]);

  // Scoring is deterministic (votes + host actions only), so the queue
  // moves ONLY when someone acts on it — no timers, no surprise shuffles
  const sortedPlaylist = useMemo(() => {
    return sortPlaylistByPriority(playlistArray, currentPlayingSongId);
  }, [playlistArray, currentPlayingSongId]);

  // Get currently playing song and queue
  const nowPlaying = sortedPlaylist.find(item => item.isPlaying);
  const upcomingQueue = sortedPlaylist.filter(item => !item.isPlaying);

  // Hero tint from the artwork; brand gradient as fallback
  const heroColor = useArtworkColor(nowPlaying?.image);

  // Guest-visible progress derived from when the host started the track
  const startedAt = session.nowPlaying?.startedAt;
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const elapsedMs = startedAt
    ? Math.min(Math.max(Date.now() - new Date(startedAt).getTime(), 0), nowPlaying?.duration || 0)
    : 0;
  const progressPct = nowPlaying?.duration ? (elapsedMs / nowPlaying.duration) * 100 : 0;

  const formatDuration = (duration) => {
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVote = (songId, voteType) => {
    const item = playlistArray.find(i => i.firebaseKey === songId);
    const currentVote = item?.userVote || 0;
    let newVote = 0;

    if (voteType === 'up') {
      newVote = currentVote === 1 ? 0 : 1;
    } else {
      newVote = currentVote === -1 ? 0 : -1;
    }

    setLocalVotes(prev => ({ ...prev, [songId]: newVote }));
    voteSong(songId, newVote);
    
    // Visual feedback
    if (newVote !== 0) {
      toast.success(newVote === 1 ? 'Upvoted!' : 'Downvoted!', {
        icon: newVote === 1 ? '👍' : '👎',
        duration: 2000
      });
    }
  };

  const handleSkipVote = (songId) => {
    const hasVoted = localSkipVotes[songId]?.includes(user?.uid);

    if (hasVoted) {
      toast.error('Already voted to skip');
      return;
    }

    setLocalSkipVotes(prev => ({
      ...prev,
      [songId]: [...(prev[songId] || []), user?.uid]
    }));

    hostAction('skipVote', null, songId);
    toast.success('Skip vote cast!', { icon: '⏭️' });
  };

  const handleHostBoost = (songId) => {
    hostAction('boostSong', null, songId);
    toast.success('Song boosted to top!', { icon: '🚀' });
  };

  const handlePlayNext = (songId) => {
    hostAction('playNext', null, songId);
    toast.success('Song will play next!', { icon: '⏭️' });
  };

  const handleRemoveFromPlaylist = (songId) => {
    hostAction('removeFromPlaylist', null, songId);
    toast.success('Song removed from playlist');
  };

  const participantCount = Object.keys(session.participants || {}).length;

  // Who upvoted a song — for the little avatar cluster on each row
  const votersFor = (key) => {
    const votes = session.votes?.[key] || {};
    return Object.entries(votes)
      .filter(([uid, v]) => !uid.startsWith('_') && v === 1)
      .map(([uid]) => session.participants?.[uid])
      .filter(Boolean);
  };

  if (playlistArray.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No songs in playlist</h3>
          <p className="text-gray-400">
            Start by suggesting some songs from the Suggestions tab!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Now Playing Hero — blurred artwork backdrop, tinted to the song */}
      {nowPlaying && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-xl border border-white/10 p-6 shadow-xl"
        >
          {nowPlaying.image && (
            <img
              src={nowPlaying.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-30"
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              background: heroColor
                ? `linear-gradient(135deg, ${heroColor.replace('rgb', 'rgba').replace(')', ', 0.45)')} 0%, rgba(30, 27, 46, 0.9) 75%)`
                : 'linear-gradient(135deg, rgba(186, 43, 226, 0.35) 0%, rgba(30, 27, 46, 0.9) 75%)'
            }}
          />

          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 mb-3">
                <span className="jh-eq" aria-hidden="true">
                  <span /><span /><span /><span />
                </span>
                <h3 className="text-lg font-semibold text-white">Now Playing</h3>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Album Art */}
              {nowPlaying.image && (
                <img
                  src={nowPlaying.image}
                  alt={nowPlaying.name}
                  className="w-24 h-24 rounded-xl object-cover shadow-2xl"
                />
              )}

              {/* Song Info */}
              <div className="flex-1 min-w-0 w-full">
                <h4 className="text-xl font-bold text-white truncate">{nowPlaying.name}</h4>
                <p className="text-gray-200 truncate">{nowPlaying.artist} • {nowPlaying.album}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-300">
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDuration(nowPlaying.duration)}
                  </span>
                </div>
                {/* Credit where it's due — the social payoff moment */}
                {nowPlaying.addedBy?.displayName && (
                  <div className="flex items-center gap-2 mt-2">
                    <Avatar
                      uid={nowPlaying.addedBy.uid}
                      name={nowPlaying.addedBy.displayName}
                      size="xs"
                    />
                    <span className="text-sm text-white/90 font-medium">
                      Added by {nowPlaying.addedBy.uid === user?.uid ? 'you' : nowPlaying.addedBy.displayName}
                      {nowPlaying.addedBy.uid === user?.uid && ' 🎉'}
                    </span>
                  </div>
                )}
                {/* Live progress, derived from the host's start time */}
                {startedAt && nowPlaying.duration > 0 && (
                  <div className="mt-3">
                    <div className="bg-white/15 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="jh-gradient h-full rounded-full transition-all duration-1000"
                        style={{ width: `${Math.min(progressPct, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-gray-300 font-mono mt-1">
                      <span>{formatDuration(elapsedMs)}</span>
                      <span>{formatDuration(nowPlaying.duration)}</span>
                    </div>
                  </div>
                )}
              </div>

            {/* Skip Vote + Progress */}
            <div className="w-full sm:w-48">
              {(() => {
                const skipProgress = calculateSkipProgress(
                  nowPlaying.skipCount || 0,
                  participantCount
                );
                const hasSkipVoted = nowPlaying.skipVotes?.includes(user?.uid);
                return (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleSkipVote(nowPlaying.firebaseKey)}
                      disabled={hasSkipVoted}
                      className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        hasSkipVoted
                          ? 'bg-orange-600/30 text-orange-300 cursor-default'
                          : 'bg-gray-700 text-gray-300 hover:bg-orange-600/30 hover:text-orange-300'
                      }`}
                    >
                      <SkipForward className="w-3.5 h-3.5" />
                      {hasSkipVoted ? 'Skip vote cast' : 'Vote to skip'}
                    </button>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Skip votes</span>
                      <span>{skipProgress.current}/{skipProgress.required}</span>
                    </div>
                    <div className="bg-gray-700 rounded-full h-2 overflow-hidden">
                      <motion.div
                        className="bg-gradient-to-r from-orange-500 to-red-500 h-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${skipProgress.percentage}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    {skipProgress.canSkip && (
                      <p className="text-xs text-red-400 animate-pulse">
                        Skipping to next song...
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Party Energy — the crowd's collective dial */}
      <PartyEnergyMeter />

      {/* Queue Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Up Next</h2>
        <span className="text-sm text-gray-400">
          {upcomingQueue.length} {upcomingQueue.length === 1 ? 'song' : 'songs'} • ordered by votes
        </span>
      </div>

      {/* Queue Items */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {upcomingQueue.map((item, index) => {
            const skipProgress = calculateSkipProgress(
              item.skipCount || 0,
              participantCount
            );
            return (
              <motion.div
                key={item.firebaseKey}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ 
                  layout: { duration: 0.3 },
                  opacity: { duration: 0.2 }
                }}
                className={`
                  bg-gray-800/80 rounded-xl p-3 border transition-all
                  ${item.playNext ? 'border-yellow-500/60 shadow-yellow-500/10 shadow-lg' :
                    item.hostBoosted ? 'border-purple-500/60 shadow-purple-500/10 shadow-lg' :
                    'border-white/5 hover:border-white/15'}
                `}
              >
                {/* Unified compact row — rank, art, info + voters, vote chips */}
                <div className="flex items-center gap-3">
                  {index === 0 ? (
                    <span className="w-6 text-right text-[9px] font-bold text-green-400 shrink-0 leading-tight">UP NEXT</span>
                  ) : (
                    <span className="w-6 text-right text-sm font-bold text-gray-500 shrink-0">{item.position}</span>
                  )}
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-11 h-11 rounded-lg object-cover shrink-0" />
                  ) : (
                    <span className="w-11 h-11 rounded-lg bg-gray-700 shrink-0 flex items-center justify-center">
                      <Music className="w-5 h-5 text-gray-500" />
                    </span>
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate text-sm flex items-center gap-1.5">
                      <span className="truncate min-w-0">{item.name}</span>
                      {item.playNext && (
                        <span className="text-[9px] bg-yellow-500 text-black px-1.5 py-0.5 rounded font-bold shrink-0">NEXT</span>
                      )}
                      {item.hostBoosted && !item.playNext && (
                        <Sparkles className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                      )}
                    </h3>
                    <p className="text-gray-400 text-xs truncate">{item.artist}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <AvatarStack people={votersFor(item.firebaseKey)} max={3} size="xs" />
                      <span className={`text-[11px] font-semibold ${getScoreColor(item.score)}`}>
                        {formatVoteDisplay(item.upvotes, item.downvotes)} {Math.abs(item.upvotes - item.downvotes) === 1 ? 'vote' : 'votes'}
                      </span>
                      <button
                        onClick={() => reactToSong(item.firebaseKey)}
                        className={`text-[11px] px-1.5 py-0.5 rounded-full transition-colors ${
                          session.songReactions?.[item.firebaseKey]?.[user?.uid]
                            ? 'bg-orange-500/30 text-orange-300'
                            : 'bg-gray-700/60 text-gray-400 hover:text-orange-300'
                        }`}
                        title="Fire this pick"
                        aria-label="React with fire"
                      >
                        🔥{Object.keys(session.songReactions?.[item.firebaseKey] || {}).length || ''}
                      </button>
                      {skipProgress.current > 0 && (
                        <span className="text-[11px] text-orange-400">
                          skip {skipProgress.current}/{skipProgress.required}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleVote(item.firebaseKey, 'up')}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        item.userVote === 1 ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-green-300'
                      }`}
                      title="Upvote"
                      aria-label="Upvote"
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleVote(item.firebaseKey, 'down')}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        item.userVote === -1 ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-red-300'
                      }`}
                      title="Downvote"
                      aria-label="Downvote"
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleSkipVote(item.firebaseKey)}
                      disabled={item.skipVotes?.includes(user?.uid)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        item.skipVotes?.includes(user?.uid)
                          ? 'bg-orange-600/40 text-orange-200'
                          : 'bg-gray-700 text-gray-400 hover:text-orange-300'
                      }`}
                      title="Vote to skip"
                      aria-label="Vote to skip"
                    >
                      <SkipForward className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Slim host action row */}
                {isHost && (
                  <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-white/5">
                    <button
                      onClick={() => handlePlayNext(item.firebaseKey)}
                      className="flex-1 py-1 rounded bg-yellow-600/15 text-yellow-400 text-[11px] font-medium hover:bg-yellow-600/25 transition-colors"
                    >
                      Play Next
                    </button>
                    <button
                      onClick={() => handleHostBoost(item.firebaseKey)}
                      className="flex-1 py-1 rounded bg-purple-600/15 text-purple-300 text-[11px] font-medium hover:bg-purple-600/25 transition-colors"
                    >
                      Boost
                    </button>
                    <button
                      onClick={() => handleRemoveFromPlaylist(item.firebaseKey)}
                      className="px-3 py-1 rounded bg-red-600/15 text-red-300 text-[11px] font-medium hover:bg-red-600/25 transition-colors"
                      aria-label="Remove from playlist"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* The night so far — collapsed so the queue stays the star */}
      {(() => {
        const played = Object.entries(session.playlist || {})
          .filter(([, s]) => s?.status === 'played')
          .map(([key, s]) => ({ ...s, firebaseKey: key }));
        if (played.length === 0) return null;
        return (
          <div className="mt-6">
            <button
              onClick={() => setShowPlayed((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-semibold text-gray-400 hover:text-white transition-colors py-2"
            >
              <span>Played earlier ({played.length})</span>
              <span>{showPlayed ? '▾' : '▸'}</span>
            </button>
            {showPlayed && (
              <div className="space-y-2 mt-1">
                {played.map((item) => (
                  <div
                    key={item.firebaseKey}
                    className="flex items-center gap-3 bg-gray-800/40 rounded-lg p-2 opacity-70"
                  >
                    {item.image && (
                      <img src={item.image} alt="" className="w-8 h-8 rounded object-cover" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-200 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.artist}
                        {item.addedBy?.displayName ? ` • ${item.addedBy.displayName}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Playlist Stats */}
      <div className="card bg-gray-800/50 mt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0 text-sm">
          <div className="text-gray-400">
            <span className="font-semibold text-white">{playlistArray.length}</span> songs • 
            Total duration: <span className="font-semibold text-white">
              {formatDuration(playlistArray.reduce((total, item) => total + (item.duration || 0), 0))}
            </span>
          </div>
          <div className="text-xs text-gray-400">
            <AlertCircle className="w-3 h-3 inline mr-1" />
            Votes decide the order — what you see is what plays next
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistEnhanced;